import { selectSharedViewDocument } from "./sharedViewSelectors";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { IViewerView } from "@multiconsult-group/multiplacer-core";
import { useAppDispatch } from "../../app/hooks";
import { setActiveSharedViewInRedux, type IViewPresetReference } from "../../ViewerRedux/viewerSlice";
import { applyViewerViewThunk } from "../viewThunks";
import { selectSharedViewState } from "../viewSelectors";
import { flushViewerCameraUpdate } from "../Viewer/cameraUpdateQueue";
import { useCreateSharedViewMutation } from "./sharedViewsApi";
import { sharedViewFingerprint, sharedViewPath } from "./sharedViewUrl";

export function useApplyAndShareView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createView, { isLoading }] = useCreateSharedViewMutation();
  const [error, setError] = useState("");
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const applyAndShare = async (view: IViewerView, preset: IViewPresetReference | null = null) => {
    if (busy.current) return;
    setError("");
    flushViewerCameraUpdate(dispatch);
    dispatch(applyViewerViewThunk(view, preset));
    const { projectNo, snapshot, active } = dispatch((_dispatch, getState) => ({
      projectNo: getState().design.projectNo,
      snapshot: selectSharedViewState(getState()),
      active: getState().viewer.activeSharedView,
    }));
    if (!snapshot || !projectNo) {
      setError("Visningen er valgt, men lenken er ikke klar. Vent til etasjen er lastet, og prøv igjen.");
      return;
    }
    // Reuse the current immutable snapshot when clicking the same view again.
    if (active && sharedViewFingerprint(active.state) === sharedViewFingerprint(snapshot)) {
      navigate(sharedViewPath(projectNo, active.id), { replace: true });
      return;
    }
    busy.current = true;
    try {
      const response = await createView({ body: { projectNo, data: { kind: "snapshot", ...snapshot } } }).unwrap();
      const saved = selectSharedViewDocument(response);
      if (!saved) throw new Error("Expected a snapshot response");
      if (!mounted.current) return;
      flushViewerCameraUpdate(dispatch);
      const latest = dispatch((_dispatch, getState) => ({
        projectNo: getState().design.projectNo,
        snapshot: selectSharedViewState(getState()),
      }));
      // A response must never replace a newer camera, floor or view selection.
      if (latest.projectNo !== projectNo || !latest.snapshot ||
        sharedViewFingerprint(latest.snapshot) !== sharedViewFingerprint(snapshot)) return;
      dispatch(setActiveSharedViewInRedux({ id: saved._id, state: { view: saved.view, context: saved.context } }));
      navigate(sharedViewPath(projectNo, saved._id), { replace: true });
    } catch {
      if (mounted.current) setError("Visningen er valgt, men lenken kunne ikke opprettes. Klikk på visningen for å prøve igjen.");
    } finally {
      busy.current = false;
    }
  };

  return { applyAndShare, isLoading, error };
}
